<script setup lang="ts">
  import { ref, computed, nextTick, onMounted } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { useToast } from '@/components/ui/toast'
  import { useRouter } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { FlagEnum } from '@/types/generated'
  import {
    EyeIcon,
    PencilIcon,
    ClockIcon,
    EllipsisIcon,
    Link2,
    Download,
    Trash2,
    Edit,
    Plus,
    FilePen
  } from 'lucide-vue-next'
  import NewPdfViewer from '@/components/pdf/NewPdfViewer.vue'
  import Button from '@/components/ui/button/Button.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import { UnifiedDocumentTemplatesService, type UnifiedDocumentTemplate } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse,
    type Filter
  } from '@/components/admin/PaginatedTable.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'

  type DocumentTemplate = UnifiedDocumentTemplate

  const { toast } = useToast()
  const router = useRouter()
  const pdfFormTemplatesFlag = useFeatureFlag(FlagEnum.PDF_FORM_TEMPLATES)
  const clientPortalV3ApiFlag = useFeatureFlag(FlagEnum.CLIENT_PORTAL_V3_API)

  const { t } = useTranslation({
    en: {
      title: 'PDF Document Templates',
      intro:
        'Manage your PDF document templates. Here you can view, rename, and preview your templates.',
      loading: 'Loading document templates...',
      error: 'Error loading document templates',
      noTemplates: 'No PDF document templates found',
      name: 'Name',
      updatedBy: 'Updated by',
      lastModified: 'Last modified',
      preview: 'Preview',
      edit: 'Edit',
      create: 'Create Template',
      copy: 'Copy link',
      download: 'Download',
      delete: 'Delete',
      deleteSuccess: 'Document template deleted successfully',
      deleteError: 'Error deleting document template',
      renameSuccess: 'Document template renamed successfully',
      renameError: 'Error renaming document template',
      copySuccess: 'Link copied',
      copyError: 'Error copying link',
      downloadError: 'Download Error',
      previewTitle: 'PDF Preview',
      fileSize: 'File size',
      sortBy: 'Sort by',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      recentlyModified: 'Recently modified',
      leastRecentlyModified: 'Least recently modified',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A'
    },
    de: {
      title: 'PDF-Dokumentenvorlagen',
      intro:
        'Verwalten Sie Ihre PDF-Dokumentenvorlagen. Hier können Sie Vorlagen anzeigen, umbenennen und in der Vorschau betrachten.',
      loading: 'Lade Dokumentenvorlagen...',
      error: 'Fehler beim Laden der Dokumentenvorlagen',
      noTemplates: 'Keine PDF-Dokumentenvorlagen gefunden',
      name: 'Name',
      updatedBy: 'Aktualisiert von',
      lastModified: 'Zuletzt geändert',
      preview: 'Vorschau',
      edit: 'Bearbeiten',
      create: 'Vorlage erstellen',
      copy: 'Link kopieren',
      download: 'Herunterladen',
      delete: 'Löschen',
      deleteSuccess: 'Dokumentenvorlage erfolgreich gelöscht',
      deleteError: 'Fehler beim Löschen der Dokumentenvorlage',
      renameSuccess: 'Dokumentenvorlage erfolgreich umbenannt',
      renameError: 'Fehler beim Umbenennen der Dokumentenvorlage',
      copySuccess: 'Link kopiert',
      copyError: 'Fehler beim Kopieren des Links',
      downloadError: 'Download-Fehler',
      previewTitle: 'PDF-Vorschau',
      fileSize: 'Dateigröße',
      sortBy: 'Sortieren nach',
      newestFirst: 'Neueste zuerst',
      oldestFirst: 'Älteste zuerst',
      recentlyModified: 'Kürzlich geändert',
      leastRecentlyModified: 'Am wenigsten geändert',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A'
    }
  })

  const editingTemplate = ref<string | null>(null)
  const editingName = ref('')
  const originalName = ref('')
  const selectedTemplate = ref<DocumentTemplate | null>(null)
  const showPreview = ref(false)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const filterableFields: Filter[] = [
    {
      key: 'ordering',
      label: t('sortBy'),
      type: 'select',
      value: '-created_at',
      options: [
        { label: t('newestFirst'), value: '-created_at' },
        { label: t('oldestFirst'), value: 'created_at' },
        { label: t('recentlyModified'), value: '-modified_at' },
        { label: t('leastRecentlyModified'), value: 'modified_at' },
        { label: t('nameAZ'), value: 'title' },
        { label: t('nameZA'), value: '-title' }
      ]
    }
  ]

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTemplateFileName = (template: DocumentTemplate): string => {
    if (template.file) {
      const match = template.file.match(/\/([^/?]+\.pdf)/i)
      return match ? match[1] : ''
    }
    return ''
  }

  // Fetch data function for PaginatedTable
  const fetchTemplates = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<DocumentTemplate>> => {
    try {
      // Get ordering from filters
      const orderingFilter = options?.filters?.find((f) => f.key === 'ordering')
      const ordering = orderingFilter?.value || '-created_at'

      const response = await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesList({
        limit: options?.limit,
        offset: options?.offset,
        search: options?.search,
        ordering: ordering
      })
      return {
        results: response.results || [],
        count: response.count || 0,
        next: response.next || null,
        previous: response.previous || null
      }
    } catch (error) {
      console.error('Error loading templates:', error)
      genericErrorToast()
      return {
        results: [],
        count: 0,
        next: null,
        previous: null
      }
    }
  }

  const editingTemplateType = ref<string | null>(null)

  const startEditingName = (template: DocumentTemplate) => {
    editingTemplate.value = template.id
    editingTemplateType.value = template.template_type
    editingName.value = template.title || ''
    originalName.value = template.title || ''

    // Focus the input field after the next tick to ensure it's rendered
    nextTick(() => {
      const input = document.querySelector(
        '[data-cy="PdfFormsSettingsTemplateNameInput"]'
      ) as HTMLInputElement
      if (input) {
        input.focus()
        input.select()
      }
    })
  }

  const cancelEditing = () => {
    editingTemplate.value = null
    editingTemplateType.value = null
    editingName.value = ''
    originalName.value = ''
  }

  const saveChanges = async () => {
    if (!editingTemplate.value) return

    const trimmedNewName = editingName.value.trim()
    if (trimmedNewName === originalName.value) {
      cancelEditing()
      return
    }

    if (!trimmedNewName) {
      cancelEditing()
      return
    }

    const templateId = editingTemplate.value
    const templateType = editingTemplateType.value
    cancelEditing()

    if (!templateType) {
      genericErrorToast()
      return
    }

    try {
      await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesPartialUpdate({
        id: templateId,
        requestBody: {
          title: trimmedNewName,
          template_type: templateType
        }
      })

      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }

      genericSuccessToast()
    } catch (error) {
      console.error('Error saving template:', error)
      genericErrorToast()

      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }
    }
  }

  const handleInputKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveChanges()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditing()
    }
  }

  const handleInputBlur = () => {
    saveChanges()
  }

  const previewTemplate = (template: DocumentTemplate) => {
    selectedTemplate.value = template
    showPreview.value = true
  }

  const closePreview = () => {
    showPreview.value = false
    selectedTemplate.value = null
  }

  const editTemplate = (template: DocumentTemplate) => {
    if (template.template_type === 'pdf_form') {
      router.push({
        path: ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_EDIT.replace(
          ':uuid',
          template.id
        )
      })
    }
  }

  const copyTemplate = (template: DocumentTemplate) => {
    let shareableLink: string

    if (template.template_type === 'pdf_form') {
      shareableLink = `${window.location.origin}/client/portal/standalone/pdf-templates/${template.external_document_creation_token}`
    } else {
      shareableLink = `${window.location.origin}/client/portal/external/pdf/${template.external_document_creation_token}`
    }

    navigator.clipboard.writeText(shareableLink)
    toast({
      title: t('copySuccess'),
      description: 'Der Link wurde in die Zwischenablage kopiert',
      variant: 'default'
    })
  }

  const downloadTemplate = (template: DocumentTemplate) => {
    if (template.file) {
      window.open(template.file, '_blank')
    } else {
      toast({
        title: t('downloadError'),
        description: 'Datei nicht verfügbar',
        variant: 'destructive'
      })
    }
  }

  const deleteTemplate = async (template: DocumentTemplate) => {
    try {
      await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesDestroy({ id: template.id })

      showPreview.value = false
      selectedTemplate.value = null

      toast({
        title: t('deleteSuccess'),
        variant: 'default'
      })

      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error deleting template:', error)
      toast({
        title: t('deleteError'),
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive'
      })
    }
  }

  const navigateToCreate = () => {
    router.push(ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_CREATE)
  }

  onMounted(async () => {
    await Promise.all([pdfFormTemplatesFlag.checkFlag(), clientPortalV3ApiFlag.checkFlag()])
  })
</script>

<template>
  <SettingsPage>
    <template #title>{{ t('title') }}</template>
    <template #description>{{ t('intro') }}</template>

    <template #actions>
      <Button
        v-if="pdfFormTemplatesFlag.isEnabled.value && clientPortalV3ApiFlag.isEnabled.value"
        data-cy="DocumentTemplatesCreateButton"
        class="flex items-center gap-2"
        @click="navigateToCreate"
      >
        <Plus class="h-4 w-4" />
        {{ t('create') }}
      </Button>
    </template>

    <div class="space-y-4">
      <PaginatedTableView
        ref="paginatedTableRef"
        :fetchData="fetchTemplates"
        searchableFields="title"
        :filterableFields="filterableFields"
      >
        <template #table-head>
          <TableCell class="py-2 font-medium">{{ t('name') }}</TableCell>
          <TableCell class="w-48 py-2 font-medium">{{ t('updatedBy') }}</TableCell>
          <TableCell class="w-40 py-2 font-medium">{{ t('lastModified') }}</TableCell>
          <TableCell class="w-12 py-2"></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="template in items"
            :key="template.id"
            class="hover:bg-gray-50"
          >
            <!-- Name column -->
            <TableCell class="py-4">
              <div
                v-if="editingTemplate === template.id"
                class="flex items-center gap-2"
              >
                <div class="flex-1">
                  <InputField
                    v-model="editingName"
                    name="templateName"
                    :placeholder="t('name')"
                    class="flex-1"
                    data-cy="PdfFormsSettingsTemplateNameInput"
                    @keydown="handleInputKeydown"
                    @blur="handleInputBlur"
                  />
                </div>
              </div>
              <div
                v-else
                class="flex items-center justify-between"
              >
                <div class="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    v-if="template.title"
                    class="max-w-32 truncate text-sm font-medium text-gray-900 sm:max-w-48 lg:max-w-64"
                  >
                    {{ template.title }}
                  </span>
                  <span
                    v-else
                    class="max-w-32 truncate text-sm font-medium text-gray-400 sm:max-w-48 lg:max-w-64"
                  >
                    {{ getTemplateFileName(template) }}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="icon"
                  class="h-6 w-6 p-0 text-gray-800 hover:text-gray-900"
                  data-cy="PdfFormsSettingsEditButton"
                  @click="startEditingName(template)"
                >
                  <PencilIcon class="h-4 w-4" />
                </Button>
              </div>
            </TableCell>

            <!-- Updated by column -->
            <TableCell class="py-4 text-sm text-gray-500">
              <div
                v-if="template.modified_by"
                class="flex items-center gap-2"
              >
                <UserAvatar
                  :user="template.modified_by"
                  size="32px"
                />
                <span class="text-gray-800">
                  {{
                    template.modified_by?.first_name && template.modified_by?.last_name
                      ? `${template.modified_by.first_name} ${template.modified_by.last_name}`.trim()
                      : template.modified_by?.first_name ||
                        template.modified_by?.last_name ||
                        'Unknown User'
                  }}
                </span>
              </div>
              <div
                v-else
                class="flex items-center gap-2"
              >
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200">
                  <span class="text-xs text-gray-400">?</span>
                </div>
                <span class="text-gray-400">N/A</span>
              </div>
            </TableCell>

            <!-- Modified date column -->
            <TableCell class="py-4 text-sm text-gray-500">
              <div class="flex items-center gap-1">
                <ClockIcon class="h-4 w-4" />
                {{ formatDate(template.modified_at) }}
              </div>
            </TableCell>

            <!-- Actions column -->
            <TableCell class="py-4">
              <div class="flex items-center justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="h-8 w-8 p-0"
                      data-cy="PdfFormsSettingsActionsButton"
                    >
                      <EllipsisIcon class="h-4 w-4" />
                      <span class="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    class="w-48"
                  >
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PdfFormsSettingsPreviewAction"
                      @click="previewTemplate(template)"
                    >
                      <EyeIcon class="mr-2 h-4 w-4" />
                      {{ t('preview') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      v-if="template.template_type === 'pdf_form'"
                      class="cursor-pointer"
                      data-cy="PdfFormsSettingsEditAction"
                      @click="editTemplate(template)"
                    >
                      <Edit class="mr-2 h-4 w-4" />
                      {{ t('edit') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PdfFormsSettingsCopyLinkAction"
                      @click="copyTemplate(template)"
                    >
                      <Link2 class="mr-2 h-4 w-4" />
                      {{ t('copy') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PdfFormsSettingsDownloadAction"
                      @click="downloadTemplate(template)"
                    >
                      <Download class="mr-2 h-4 w-4" />
                      {{ t('download') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer text-red-600 focus:text-red-600"
                      data-cy="PdfFormsSettingsDeleteAction"
                      @click="deleteTemplate(template)"
                    >
                      <Trash2 class="mr-2 h-4 w-4" />
                      {{ t('delete') }}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>

    <!-- PDF Preview Modal -->
    <NewPdfViewer
      v-model:is-open="showPreview"
      :url="selectedTemplate?.file || ''"
      :filename="selectedTemplate?.title || ''"
      :document="{
        filesize: 0,
        page_count: 0
      }"
      :initial-page="1"
      :annotation="false"
      :form-fields="
        selectedTemplate?.template_type === 'pdf_form' ? selectedTemplate?.form_fields || [] : []
      "
      :show-form-fields="selectedTemplate?.template_type === 'pdf_form'"
      :disable-edit-page="true"
      :hide-topbar="true"
      @update:is-open="closePreview"
    >
      <template #customToolbar>
        <div class="flex items-center space-x-3">
          <Button
            v-if="selectedTemplate?.template_type === 'pdf_form'"
            variant="secondary"
            size="sm"
            @click="editTemplate(selectedTemplate)"
          >
            <FilePen class="mr-2 h-5 w-5" />
            {{ t('edit') }}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            @click="downloadTemplate(selectedTemplate!)"
          >
            <Download class="mr-2 h-5 w-5" />
            {{ t('download') }}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            @click="copyTemplate(selectedTemplate!)"
          >
            <Link2 class="mr-2 h-5 w-5" />
            {{ t('copy') }}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            class="text-red-600 hover:text-red-700"
            @click="deleteTemplate(selectedTemplate!)"
          >
            <Trash2 class="mr-2 h-4 w-4" />
            {{ t('delete') }}
          </Button>
        </div>
      </template>
    </NewPdfViewer>
  </SettingsPage>
</template>
